import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Check, AlertCircle, Plus, Minus } from "lucide-react";
import { resolveMedia } from "@/utils/mediaResolver";

/**
 * ModifierSelectionModal - Professional modifier selection for POS
 *
 * Shows modifier groups with their options and allows customer/staff to select
 * according to the defined rules (min/max, required, etc.)
 * Each option has a +/- stepper for quantity.
 */
export function ModifierSelectionModal({
  open,
  onOpenChange,
  product,
  modifierGroups = [],
  onConfirm,
  loading = false
}) {
  // selections: { groupId: { optionId: quantity } }
  const [selections, setSelections] = useState({});
  const [errors, setErrors] = useState({});

  // Reset selections when product changes
  useEffect(() => {
    if (open && product) {
      const initial = {};
      modifierGroups.forEach(group => {
        initial[group.id] = {};
      });
      setSelections(initial);
      setErrors({});
    }
  }, [open, product, modifierGroups]);

  const getOptionQty = (groupId, optionId) =>
    (selections[groupId] || {})[optionId] || 0;

  const getGroupTotalQty = (groupId) =>
    Object.values(selections[groupId] || {}).reduce((sum, q) => sum + q, 0);

  const changeOptionQty = (groupId, optionId, delta) => {
    setSelections(prev => {
      const groupSel = { ...(prev[groupId] || {}) };
      const newQty = Math.max(0, (groupSel[optionId] || 0) + delta);
      if (newQty === 0) {
        delete groupSel[optionId];
      } else {
        groupSel[optionId] = newQty;
      }
      return { ...prev, [groupId]: groupSel };
    });
  };

  // Validate all selections
  const validateSelections = () => {
    const newErrors = {};
    let isValid = true;

    modifierGroups.forEach(group => {
      const total = getGroupTotalQty(group.id);
      if (group.required && total < group.min_select) {
        newErrors[group.id] = `Seleccione al menos ${group.min_select}`;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validateSelections()) return;

    const selectedModifiers = [];
    const selectedVariations = [];
    let extraPrice = 0;

    modifierGroups.forEach(group => {
      const groupSel = selections[group.id] || {};
      const optionsWithQty = [];

      Object.entries(groupSel).forEach(([optionId, qty]) => {
        if (qty > 0) {
          const opt = group.options.find(o => o.id === optionId);
          if (opt) {
            optionsWithQty.push({ ...opt, qty });
            extraPrice += (opt.price || 0) * qty;
            selectedVariations.push({
              name: opt.name,
              quantity: qty,
              price: opt.price || 0,
              group: group.name
            });
          }
        }
      });

      if (optionsWithQty.length > 0) {
        selectedModifiers.push({
          group_id: group.id,
          group_name: group.name,
          options: optionsWithQty.map(o => ({
            id: o.id,
            name: o.name,
            price: o.price || 0,
            quantity: o.qty
          }))
        });
      }
    });

    onConfirm({
      product,
      modifiers: selectedModifiers,
      selected_variations: selectedVariations,
      extra_price: extraPrice,
      total_price: (product.price || 0) + extraPrice
    });
  };

  // Calculate total price
  const calculateTotal = () => {
    let extra = 0;
    modifierGroups.forEach(group => {
      Object.entries(selections[group.id] || {}).forEach(([optionId, qty]) => {
        const opt = group.options.find(o => o.id === optionId);
        if (opt) extra += (opt.price || 0) * qty;
      });
    });
    return (product?.price || 0) + extra;
  };

  // Check if all required groups are satisfied
  const allRequiredSatisfied = () => {
    return modifierGroups.every(group => {
      if (!group.required) return true;
      return getGroupTotalQty(group.id) >= group.min_select;
    });
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md sm:max-w-lg p-0 overflow-hidden">
        {/* Header with product info */}
        <DialogHeader className="p-4 pb-0 border-b bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-start gap-3">
            {product.pos_media_url || product.image_url ? (
              <img
                src={resolveMedia(product.pos_media_url || product.image_url)}
                alt={product.name}
                className="w-16 h-16 rounded-lg object-cover shadow-sm"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                <span className="text-2xl">🍽️</span>
              </div>
            )}
            <div className="flex-1">
              <DialogTitle className="text-lg font-bold text-gray-900">{product.name}</DialogTitle>
              <p className="text-sm text-gray-500 mt-0.5">{product.description || "Personalize seu pedido"}</p>
              <div className="mt-1">
                <span className="text-lg font-bold text-emerald-600">{product.price?.toFixed(2) || "0.00"} EUR</span>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Modifier groups */}
        <ScrollArea className="max-h-[50vh] p-4">
          <div className="space-y-4">
            {modifierGroups.map(group => {
              const totalQty = getGroupTotalQty(group.id);
              const isComplete = totalQty >= group.min_select;
              const hasError = errors[group.id];

              return (
                <div
                  key={group.id}
                  className={`bg-white rounded-lg border-2 p-3 transition ${hasError ? "border-red-300" : isComplete && group.required ? "border-emerald-200" : "border-gray-100"}`}
                  data-testid={`modifier-group-${group.id.slice(-6)}`}
                >
                  {/* Group header */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{group.name}</h3>
                      <p className="text-xs text-gray-500">
                        {group.required ? (
                          group.min_select === group.max_select
                            ? `Elija ${group.min_select}`
                            : `Elija ${group.min_select} a ${group.max_select}`
                        ) : (
                          `Opcional (hasta ${group.max_select})`
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {group.required && (
                        <Badge variant={isComplete ? "default" : "outline"} className={isComplete ? "bg-emerald-100 text-emerald-700" : "border-orange-300 text-orange-600"}>
                          {isComplete ? <Check className="w-3 h-3 mr-1" /> : null}
                          {totalQty}/{group.min_select}
                        </Badge>
                      )}
                      {!group.required && totalQty > 0 && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-600">
                          {totalQty} selecc.
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Error message */}
                  {hasError && (
                    <div className="flex items-center gap-1 text-red-500 text-xs mb-2">
                      <AlertCircle className="w-3 h-3" />
                      {hasError}
                    </div>
                  )}

                  {/* Options */}
                  <div className="space-y-2">
                    {group.options.filter(opt => opt.available !== false).map(option => {
                      const qty = getOptionQty(group.id, option.id);
                      const isSelected = qty > 0;

                      return (
                        <div
                          key={option.id}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                            isSelected
                              ? "bg-emerald-50 border-emerald-300 shadow-sm"
                              : "bg-gray-50 border-gray-200"
                          }`}
                          data-testid={`option-${option.id.slice(-6)}`}
                        >
                          {/* Option image */}
                          {option.image_url && (
                            <img
                              src={resolveMedia(option.image_url)}
                              alt={option.name}
                              className="w-10 h-10 rounded object-cover"
                            />
                          )}

                          {/* Option info */}
                          <div className="flex-1 text-left">
                            <span className={`font-medium ${isSelected ? "text-emerald-800" : "text-gray-700"}`}>
                              {option.name}
                            </span>
                            {option.description && (
                              <p className="text-xs text-gray-500">{option.description}</p>
                            )}
                          </div>

                          {/* Price */}
                          {option.price > 0 && (
                            <span className={`text-sm font-semibold ${isSelected ? "text-emerald-600" : "text-gray-500"}`}>
                              +{option.price.toFixed(2)}EUR
                            </span>
                          )}

                          {/* Stepper */}
                          <div className="flex items-center gap-1 ml-1">
                            {qty > 0 && (
                              <>
                                <button
                                  onClick={() => changeOptionQty(group.id, option.id, -1)}
                                  className="w-7 h-7 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 active:scale-95 font-bold text-lg leading-none"
                                >
                                  −
                                </button>
                                <span className="w-6 text-center font-bold text-emerald-700">{qty}</span>
                              </>
                            )}
                            <button
                              onClick={() => changeOptionQty(group.id, option.id, 1)}
                              className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center hover:bg-emerald-200 active:scale-95 font-bold text-lg leading-none"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        {/* Footer with total and confirm */}
        <DialogFooter className="p-4 pt-3 border-t bg-gray-50 flex-row items-center justify-between gap-4">
          <div>
            <p className="text-xs text-gray-500">Total do produto</p>
            <p className="text-xl font-bold text-emerald-600">{calculateTotal().toFixed(2)} EUR</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={loading || !allRequiredSatisfied()}
              className="bg-emerald-600 hover:bg-emerald-700 px-6"
              data-testid="confirm-modifiers-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
